import { ChangeEvent, FC } from 'react';
import { FocusTheme } from '@/components/Focus/configs/theme';
import { PolarisIcon } from '@/exports/columnCommonComponents';
import { Box, InputBase, styled } from '@mui/material';
import SelectDropdownControl from '@/components/Focus/common/SelectDropdownControl';

type IncrementalLaunchOffsetControlProps = {
  value: number;
  selectedOption: string;
  options: string[];
  onValueChange: (value: number) => void;
  onOptionChange: (value: string) => void;
};

const MAX_DIGITS = 4;

const IncrementalLaunchOffsetControl: FC<IncrementalLaunchOffsetControlProps> = ({
  value,
  selectedOption,
  options,
  onValueChange,
  onOptionChange
}) => {
  const isDecrementDisabled = value <= 0;

  const handleDecrement = () => {
    if (isDecrementDisabled) {
      return;
    }

    onValueChange(value - 1);
  };

  const handleIncrement = () => {
    onValueChange(value + 1);
  };

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const inputValue = event.target.value;

    if (inputValue === '') {
      onValueChange(0);
      return;
    }

    if (!/^\d+$/.test(inputValue) || inputValue.length > MAX_DIGITS) {
      return;
    }

    onValueChange(Number(inputValue));
  };

  return (
    <OffsetControlContainer>
      <OffsetStepper>
        <OffsetIconButton $disabled={isDecrementDisabled} onClick={handleDecrement}>
          <PolarisIcon icon="minus_circle" width={17} />
        </OffsetIconButton>
        <OffsetValueBox>
          <OffsetValueInput
            type="text"
            value={value}
            onChange={handleInputChange}
            inputProps={{
              inputMode: 'numeric',
              maxLength: MAX_DIGITS
            }}
          />
        </OffsetValueBox>
        <OffsetIconButton $disabled={false} onClick={handleIncrement}>
          <PolarisIcon icon="add_circle" width={16} />
        </OffsetIconButton>
      </OffsetStepper>
      <SelectDropdownControl value={selectedOption} onChange={onOptionChange} options={options} minWidth={100} />
    </OffsetControlContainer>
  );
};

export default IncrementalLaunchOffsetControl;

const OffsetControlContainer = styled(Box)`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
`;

const OffsetStepper = styled(Box)`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(0.5)};
  padding: ${({ theme }) => theme.spacing(1)};
  background: ${({ theme }) => (theme as FocusTheme).palette.background.paper};
  border-radius: 4px;
`;

const OffsetIconButton = styled(Box, {
  shouldForwardProp: (prop) => prop !== '$disabled'
})<{ $disabled: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme, $disabled }) => ($disabled ? (theme as FocusTheme).palette.neutral[70] : (theme as FocusTheme).palette.neutral[30])};
  opacity: ${({ $disabled }) => ($disabled ? 0.45 : 1)};
  cursor: ${({ $disabled }) => ($disabled ? 'default' : 'pointer')};
`;

const OffsetValueBox = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 24px;
  min-width: 36px;
  background: ${({ theme }) => (theme as FocusTheme).palette.neutral[70]};
  border: 1px solid ${({ theme }) => (theme as FocusTheme).palette.neutral[100]};
  border-radius: 4px;
  padding: 0 ${({ theme }) => theme.spacing(0.5)};
  transition: border-color 0.15s ease;

  &:hover {
    border-color: ${({ theme }) => (theme as FocusTheme).palette.neutral[30]};
  }

  &:focus-within {
    border-color: ${({ theme }) => (theme as FocusTheme).palette.neutral[10]};
  }
`;

const OffsetValueInput = styled(InputBase)`
  width: 36px;

  & .MuiInputBase-input {
    padding: 0;
    text-align: center;
    font-weight: 700;
    color: ${({ theme }) => (theme as FocusTheme).palette.neutral[10]};
    font-size: 1rem;
    line-height: 24px;
    outline: none;
  }
`;
